import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { currencyUtils } from 'skipify-types';

import { useGetAllMITsQuery } from '../../../store/modules/mit/api';
import { MIT, GetAllMITsSortQuery, GetAllMITsQueryFilters } from '../../../store/modules/mit/types';
import { useMerchant } from '../../../contexts/MerchantContext';
import { DataTable, Column, getSortableFields } from '../../../common/DataTable';
import { Pagination } from '../../../common/DataTable/Pagination';
import { MITStatusChip } from '../components/StatusChip/MITStatusChip';
import { sanitizeNumericString } from '../../Paylinks/SendPaylinkForm/formValues';
import { formatCurrency } from '../../../util/formatters';
import { CardOnFileCell } from '../../../common/Customers/CardOnFileCell';
import { MITActionsPopover } from '../components/ActionsPopover';
import { MITFiltersPanel } from '../components/MITFiltersPanel/MITFiltersPanel';
import { MITFiltersFormValues, mitFiltersEmptyValues } from '../../../hooks/useMITFiltersForm';
import { useURLCustomerFetch } from '../../Paylinks/hooks';
import { TableHeader } from './TableHeader';
import { getDisplayedMITCustomerInfo } from '../../../util/helpers';
import { PaylinkCustomerInfoResponse } from '../../../store/modules/paylink/types';

const columns: Column<MIT>[] = [
  {
    id: 'status',
    headerName: 'Status',
    renderCell: (row) => <MITStatusChip status={row.status} size="small" />,
    maxWidth: 100,
    minWidth: 100,
  },
  {
    headerName: 'Customer',
    field: 'customer',
    valueGetter: (row) => getDisplayedMITCustomerInfo(row.customer),
    minWidth: 150,
    maxWidth: 250,
  },
  {
    headerName: 'Date',
    id: 'createdAt_date',
    valueGetter: (row) => dayjs(row.createdAt).format('M/DD/YY'),
    maxWidth: 100,
    sortable: true,
    sortName: 'createdAt',
  },
  {
    headerName: 'Time',
    id: 'createdAt_time',
    valueGetter: (row) => dayjs(row.createdAt).format('h:mm A'),
    maxWidth: 100,
  },
  {
    id: 'amount',
    headerName: 'Amount',
    valueGetter: (row) => {
      return formatCurrency(currencyUtils.minorToMajor(row.amount), row.currencyCode);
    },
    maxWidth: 140,
  },
  {
    id: 'cardOnFile',
    headerName: 'Card on File',
    renderCell: (row) => (
      <CardOnFileCell
        networkType={row.paymentMethod?.details?.networkType}
        lastFour={row.paymentMethod?.details?.lastFour}
        cardArtUrl={row.paymentMethod?.details?.cardArtUrl}
        variant="compact"
      />
    ),
    maxWidth: 130,
    minWidth: 130,
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 80,
    maxWidth: 150,
  },
  {
    field: 'merchantReference',
    headerName: 'Merchant Reference',
    minWidth: 80,
    maxWidth: 150,
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    valueGetter: (row) => row.createdBy,
    minWidth: 160,
    maxWidth: 300,
  },
  {
    id: 'actions',
    headerName: '',
    maxWidth: 60,
    minWidth: 60,
    renderCell: (row) => <MITActionsPopover mit={row} />,
  },
];

const sortableFields = getSortableFields(columns);

const filterKeys: Array<keyof GetAllMITsQueryFilters> = [
  'status',
  'createdBefore',
  'createdAfter',
  'amountGTE',
  'amountLTE',
  'customerIds',
];

const convertToPanelProps = (
  initialFilterState: Record<string, string>,
  customers: PaylinkCustomerInfoResponse[],
): Partial<MITFiltersFormValues> => {
  const initialState: Partial<MITFiltersFormValues> = {
    customers,
  };

  initialState.status = initialFilterState.status ? initialFilterState.status.split(',') : [];

  if (initialFilterState.createdBefore) {
    initialState.createdBefore = dayjs(initialFilterState.createdBefore);
  }

  if (initialFilterState.createdAfter) {
    initialState.createdAfter = dayjs(initialFilterState.createdAfter);
  }

  if (initialFilterState.amountGTE) {
    initialState.amountGTE = `${initialFilterState.amountGTE}`;
  }

  if (initialFilterState.amountLTE) {
    initialState.amountLTE = `${initialFilterState.amountLTE}`;
  }

  return initialState;
};

export const ViewAllTransactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { merchantId } = useMerchant();
  const initialCustomers = useURLCustomerFetch({ merchantId, customerIdKey: 'customerIds' });
  const [pageNumber, setPageNumber] = useState(
    Number(sanitizeNumericString(searchParams.get('pageNumber'))) || 1,
  );
  const [pageSize, setPageSize] = useState(
    Number(sanitizeNumericString(searchParams.get('pageSize'))) || 10,
  );
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [sortQuery, setSortQuery] = useState<GetAllMITsSortQuery>({
    sortBy: sortableFields.find((field) => field === searchParams.get('sortBy'))
      ? (searchParams.get('sortBy') as GetAllMITsSortQuery['sortBy'])
      : 'createdAt',
    orderBy: ['asc', 'desc'].find((order) => order === searchParams.get('orderBy'))
      ? (searchParams.get('orderBy') as GetAllMITsSortQuery['orderBy'])
      : 'desc',
  });

  const initialFilterState: Record<string, string> = {};
  filterKeys.forEach((key) => {
    if (searchParams.get(key)) {
      initialFilterState[key] = searchParams.get(key) as any;
    }
  });

  const [filters, setFilters] = useState<GetAllMITsQueryFilters>(initialFilterState);

  const queryParams = useMemo(() => {
    return {
      pageNumber,
      pageSize,
      ...filters,
      ...sortQuery,
      search: searchQuery?.length > 0 ? searchQuery : undefined,
    };
  }, [pageNumber, pageSize, filters, sortQuery, searchQuery]);

  const { data: response, isFetching } = useGetAllMITsQuery(
    { merchantId, ...queryParams },
    { skip: !merchantId },
  );

  useEffect(() => {
    // remove any filter keys that are no longer present in queryParams
    filterKeys.forEach((filterKey) => {
      if (!(filterKey in queryParams)) {
        searchParams.delete(filterKey);
      }
    });

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value) searchParams.set(key, value as string);
      else searchParams.delete(key);
    });

    setSearchParams(searchParams, { replace: true });
  }, [queryParams, searchParams, setSearchParams]);

  const tableRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Stack gap={2} sx={{ mt: 4 }}>
        <TableHeader
          onSearch={(query) => {
            setPageNumber(1);
            setSearchQuery(query);
          }}
          filtersCount={Object.keys(filters).length}
          onFiltersClick={() => setIsFiltersPanelOpen(true)}
        />
        <DataTable<MIT>
          rows={response?.data ?? []}
          columns={columns}
          getRowId={(row) => row.mitId}
          isLoading={isFetching}
          pageSize={pageSize}
          sort={sortQuery}
          setSort={(query) => setSortQuery(query as GetAllMITsSortQuery)}
          tableHeight="calc(100vh - 315px)"
          noRowsMessage="No transactions found. Try changing the search term."
          tableRef={tableRef}
          renderPagination={() => (
            <Pagination
              itemCount={response?.data.length ?? 0}
              itemTotal={response?.metadata.totalRecords ?? 0}
              pageCount={response?.metadata.totalPages ?? 0}
              onPageChange={(_, p) => {
                setPageNumber(p);
                tableRef.current?.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
              }}
              page={pageNumber}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => {
                setPageNumber(1);
                setPageSize(newPageSize);
              }}
              disabled={isFetching}
            />
          )}
        />
      </Stack>
      {(!initialFilterState.customerIds || !!initialCustomers.length) && (
        <MITFiltersPanel
          isOpen={isFiltersPanelOpen}
          onClose={() => setIsFiltersPanelOpen(false)}
          filters={{
            ...mitFiltersEmptyValues,
            ...convertToPanelProps(initialFilterState, initialCustomers),
          }}
          onApplyFilters={(newFilters) => {
            setPageNumber(1);
            // Convert form values to URL-serializable format
            const queryFilters: GetAllMITsQueryFilters = {};

            if (newFilters.status && newFilters.status.length > 0) {
              queryFilters.status = newFilters.status.join(',');
            }

            if (newFilters.createdAfter) {
              queryFilters.createdAfter = newFilters.createdAfter.toISOString();
            }

            if (newFilters.createdBefore) {
              queryFilters.createdBefore = newFilters.createdBefore.toISOString();
            }

            if (newFilters.amountGTE) {
              queryFilters.amountGTE = sanitizeNumericString(newFilters.amountGTE);
            }

            if (newFilters.amountLTE) {
              queryFilters.amountLTE = sanitizeNumericString(newFilters.amountLTE);
            }

            if (newFilters.customers && newFilters.customers.length > 0) {
              queryFilters.customerIds = newFilters.customers
                .map((c: any) => c.payLinkCustomerId)
                .join(',');
            }

            setFilters(queryFilters);
          }}
        />
      )}
    </>
  );
};
